import { useState, useEffect, useCallback } from 'react';
import { Activity, Trash2, BarChart3, AlertTriangle, RefreshCw, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { usePermissions } from '../../hooks/usePermissions';
import { usersApi } from '../../services/api/endpoints/users';

interface ActivityStats {
  totalLogs: number;
  estimatedSizeKB: number;
  oldestLog: string | null;
  newestLog: string | null;
  limitReached: boolean;
}

interface ActivityLog {
  id: string;
  type: string;
  description: string;
  ipAddress?: string;
  userAgent?: string;
  userId: string;
  organizationId: string;
  createdAt: string;
  metadata?: Record<string, any>;
}

export function ActivityLogsPage() {
  const { isAdmin } = usePermissions();
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgeResult, setPurgeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [filterType, setFilterType] = useState<string>('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [activitiesRes, statsRes] = await Promise.all([
        usersApi.getAllActivities(200),
        usersApi.getActivityStats(),
      ]);

      if (activitiesRes.success && activitiesRes.data) {
        setActivities(Array.isArray(activitiesRes.data) ? activitiesRes.data : []);
      }
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin, loadData]);

  const handlePurge = async () => {
    if (purgeConfirmText !== 'SUPPRIMER') return;
    setPurging(true);
    setPurgeResult(null);
    try {
      const res = await usersApi.purgeActivities(7);
      const deleted = res.success && res.data ? res.data.deletedCount : 0;
      setPurgeResult({ success: true, message: `${deleted} logs supprimés avec succès` });
      setTimeout(() => {
        setShowPurgeConfirm(false);
        setPurgeConfirmText('');
        setPurgeResult(null);
        loadData();
      }, 2000);
    } catch {
      setPurgeResult({ success: false, message: 'Erreur lors de la purge' });
    } finally {
      setPurging(false);
    }
  };

  const filteredActivities = filterType === 'all'
    ? activities
    : activities.filter(a => a.type === filterType);

  const activityTypes = [...new Set(activities.map(a => a.type))].filter(Boolean);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const prepareExportData = () => {
    return filteredActivities.map(a => ({
      Date: formatDate(a.createdAt),
      Type: getActivityTypeLabel(a.type),
      Description: a.description || '',
      IP: a.ipAddress || '',
      Utilisateur: a.userId || '',
    }));
  };

  const exportToXlsx = () => {
    const data = prepareExportData();
    if (!data.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logs');
    XLSX.writeFile(wb, `activity_logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportToCsv = () => {
    const data = prepareExportData();
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${String((row as any)[h] || '').replace(/"/g, '""')}"`).join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `activity_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    try {
      document.body.removeChild(link);
    } catch (e) {
      // Ignore if already removed
    }
    URL.revokeObjectURL(url);
  };

  const getActivityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      login: 'Connexion',
      logout: 'Déconnexion',
      profile_update: 'Mise à jour profil',
      password_change: 'Changement mot de passe',
      settings_change: 'Changement paramètres',
      api_access: 'Accès API',
      data_export: 'Export de données',
      report_generation: 'Génération rapport',
      failed_login: 'Connexion échouée',
      two_factor_enabled: '2FA activé',
      two_factor_disabled: '2FA désactivé',
      role_change: 'Changement de rôle',
      status_change: 'Changement de statut',
    };
    return labels[type] || type;
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Logs d'activité</h1>
        <Card className="p-8 text-center">
          <Activity className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Accès réservé</h2>
          <p className="text-muted-foreground">Seul un administrateur peut consulter les logs d'activité.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Logs d'activité</h1>
            <p className="text-sm text-muted-foreground">
              Historique des actions des utilisateurs de votre organisation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button variant="outline" onClick={exportToXlsx} disabled={filteredActivities.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            XLSX
          </Button>
          <Button variant="outline" onClick={exportToCsv} disabled={filteredActivities.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="destructive" onClick={() => setShowPurgeConfirm(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Purger
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <BarChart3 className="h-4 w-4" /> Total logs
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.totalLogs}</p>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Stockage estimé</div>
            <p className={`text-2xl font-bold ${stats.limitReached ? 'text-destructive' : 'text-foreground'}`}>
              {stats.estimatedSizeKB} KB
            </p>
            <p className="text-xs text-muted-foreground">/ 1024 KB max</p>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Log le plus ancien</div>
            <p className="text-sm font-medium text-foreground">
              {stats.oldestLog ? formatDate(stats.oldestLog) : '—'}
            </p>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Log le plus récent</div>
            <p className="text-sm font-medium text-foreground">
              {stats.newestLog ? formatDate(stats.newestLog) : '—'}
            </p>
          </Card>
        </div>
      )}

      {stats?.limitReached && (
        <div className="bg-warning/10 border border-warning rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" />
          <span className="text-sm text-warning">
            Limite de stockage atteinte (1 MB). Les anciens logs sont automatiquement supprimés.
          </span>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <label className="text-sm text-muted-foreground">Filtrer par type :</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground"
        >
          <option value="all">Tous</option>
          {activityTypes.map(type => (
            <option key={type} value={type}>{getActivityTypeLabel(type)}</option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground ml-2">
          {filteredActivities.length} résultat{filteredActivities.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted rounded" />
          ))}
        </div>
      ) : filteredActivities.length === 0 ? (
        <Card className="p-8 text-center">
          <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Aucun log d'activité enregistré.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Description</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredActivities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">
                      {formatDate(activity.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                        {getActivityTypeLabel(activity.type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground max-w-xs truncate">
                      {activity.description || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                      {activity.ipAddress || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Purge confirmation modal */}
      {showPurgeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="p-6 w-full max-w-md space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Purger les logs d'activité
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Cette action supprimera tous les logs antérieurs à <strong>7 jours</strong>.
                  Cette opération est irréversible.
                </p>
              </div>
            </div>

            {purgeResult && (
              <div className={`p-3 rounded-lg text-sm ${
                purgeResult.success
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}>
                {purgeResult.message}
              </div>
            )}

            {!purgeResult?.success && (
              <div className="space-y-2">
                <p className="text-sm text-foreground">
                  Pour confirmer, tapez{' '}
                  <code className="font-mono font-semibold bg-muted px-1.5 py-0.5 rounded text-destructive">
                    SUPPRIMER
                  </code>{' '}
                  ci-dessous :
                </p>
                <input
                  type="text"
                  value={purgeConfirmText}
                  onChange={(e) => setPurgeConfirmText(e.target.value)}
                  placeholder="SUPPRIMER"
                  autoComplete="off"
                  disabled={purging}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive bg-background text-foreground disabled:opacity-50"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPurgeConfirm(false);
                  setPurgeConfirmText('');
                  setPurgeResult(null);
                }}
                disabled={purging}
              >
                Annuler
              </Button>
              {!purgeResult?.success && (
                <Button
                  variant="destructive"
                  onClick={handlePurge}
                  disabled={purgeConfirmText !== 'SUPPRIMER' || purging}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {purging ? 'Suppression...' : 'Purger les logs'}
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
